import type { SignedUpAs } from '@/store/auth/auth.dto';

export enum UserRole {
  OWNER = 'owner',
  /** Every owner but the first. The owner's rights except DeleteVessel and TransferVessel. */
  CO_OWNER = 'co-owner',
  CAPTAIN = 'captain',
  ENGINEER = 'engineer',
  CREW_MEMBER = 'crew',
  CHEF = 'chef',
  MATE_DECKHAND = 'mate',
  STEWARD = 'steward',
  SERVICE_PROVIDER = 'service-provider',
  GUEST = 'guest',
}

/**
 * The people who own the vessel: its one owner — the primary, who holds the plan — and every
 * co-owner. What an owner sees, a co-owner sees too; deleting the vessel and handing ownership
 * over stay with the owner, and the backend enforces that through permissions, not roles.
 */
export const OWNER_GROUP_ROLES: readonly UserRole[] = [UserRole.OWNER, UserRole.CO_OWNER];

export const isOwnerGroupRole = (role?: string | null): boolean =>
  role === UserRole.OWNER || role === UserRole.CO_OWNER;

export const SYSTEM_ROLES = new Set<string>([
  UserRole.OWNER,
  UserRole.CO_OWNER,
  UserRole.SERVICE_PROVIDER,
  UserRole.GUEST,
]);

export const CREW_ROLES: readonly UserRole[] = [
  UserRole.CAPTAIN,
  UserRole.CHEF,
  UserRole.MATE_DECKHAND,
  UserRole.STEWARD,
];

export const OFFICER_INELIGIBLE_ROLES: ReadonlySet<UserRole> = new Set([
  UserRole.OWNER,
  UserRole.CO_OWNER,
  UserRole.CAPTAIN,
  UserRole.SERVICE_PROVIDER,
  UserRole.GUEST,
]);

const ROLE_HIERARCHY: UserRole[] = [
  UserRole.OWNER,
  UserRole.CO_OWNER,
  UserRole.CAPTAIN,
  UserRole.ENGINEER,
  UserRole.MATE_DECKHAND,
  UserRole.CHEF,
  UserRole.STEWARD,
  UserRole.CREW_MEMBER,
  UserRole.SERVICE_PROVIDER,
  UserRole.GUEST,
];

/**
 * Backend participant `type` accepts only these (oneof). Matches both
 * TaskParticipantType and EventParticipantType string values.
 */
export type ParticipantType = 'guest' | 'crew' | 'service_provider';

/** A person's role is not a participant type — collapse it to a valid one. */
export const roleToParticipantType = (role?: string | null): ParticipantType => {
  if (role === UserRole.GUEST) return 'guest';
  if (role === UserRole.SERVICE_PROVIDER) return 'service_provider';
  return 'crew';
};

export const normalizeRole = (role?: string | UserRole): UserRole | undefined => {
  if (!role) return undefined;
  const values = Object.values(UserRole) as string[];
  return values.includes(role) ? (role as UserRole) : undefined;
};

export const normalizeRoles = (roles?: readonly (string | UserRole)[] | null): UserRole[] => {
  if (!roles?.length) return [];
  const normalized = roles
    .map((role) => normalizeRole(role))
    .filter((role): role is UserRole => !!role);
  return Array.from(new Set(normalized));
};

export const hasRole = (
  userRoles: readonly UserRole[] | undefined,
  required: UserRole,
): boolean => {
  if (!userRoles) return false;
  return userRoles.includes(required);
};

/**
 * The roles the person holds on one named service, rather than on whichever one is active.
 *
 * `session.userRoles` is the active service's, which is the right answer almost everywhere —
 * but a screen showing a vessel other than the active one (the vessel switcher navigates
 * without switching accounts) has to ask about the vessel it is drawing.
 */
export const rolesOnService = (
  services: readonly { service_id: string; role: string }[] | undefined,
  serviceId: string | undefined,
): UserRole[] =>
  serviceId
    ? normalizeRoles(
        (services ?? [])
          .filter((service) => service.service_id === serviceId)
          .map((service) => service.role),
      )
    : [];

/**
 * A guest on every account they have, or on none yet: across all services, unlike `userRoles`,
 * which is only the active one's.
 */
export const isGuestOnly = (services: readonly { role: string }[] | undefined): boolean =>
  normalizeRoles(services?.map((service) => service.role)).every((role) => role === UserRole.GUEST);

/**
 * Whether Discover is hidden: a guest on every account they have. Someone on no account yet
 * falls back to what they said at signup, so a `null` or missing answer (older sessions)
 * shows everything. The `services?.length` check matters: `isGuestOnly([])` is true.
 */
export const isGuestAudience = (
  services: readonly { role: string }[] | undefined,
  signedUpAs: SignedUpAs | null | undefined,
): boolean => (services?.length ? isGuestOnly(services) : signedUpAs === 'guest');

/** Owner or co-owner on the active service. */
export const isInOwnerGroup = (userRoles: readonly UserRole[] | undefined): boolean =>
  hasAnyRole(userRoles, OWNER_GROUP_ROLES);

export const hasAnyRole = (
  userRoles: readonly UserRole[] | undefined,
  roles: readonly UserRole[],
): boolean => {
  if (!userRoles) return false;
  return roles.some((role) => userRoles.includes(role));
};

/**
 * Owner, Captain and Crew read a confirmed guest's full profile so the whole team can
 * prepare for them; the people the vessel hosts or hires in do not.
 */
export const canViewGuestProfile = (userRoles: readonly UserRole[] | undefined): boolean =>
  !!userRoles?.length && !hasAnyRole(userRoles, [UserRole.GUEST, UserRole.SERVICE_PROVIDER]);

export const hasAllRoles = (
  userRoles: readonly UserRole[] | undefined,
  roles: readonly UserRole[],
): boolean => {
  if (!userRoles) return false;
  return roles.every((role) => userRoles.includes(role));
};

export const getPrimaryRole = (userRoles: readonly UserRole[] | undefined): UserRole | null => {
  if (!userRoles?.length) return null;

  for (const role of ROLE_HIERARCHY) {
    if (userRoles.includes(role)) {
      return role;
    }
  }

  return null;
};

/**
 * Sorts items by seniority (most senior first).
 * Pass a `getRoles` extractor so this works with any shape.
 * Ordering is defined by ROLE_HIERARCHY above — edit that array to change precedence.
 */
export const sortBySeniority = <T>(items: T[], getRoles: (item: T) => readonly UserRole[]): T[] => {
  const rank = (item: T) => {
    const role = getPrimaryRole(getRoles(item));
    return role === null ? ROLE_HIERARCHY.length : ROLE_HIERARCHY.indexOf(role);
  };
  return [...items].sort((a, b) => rank(a) - rank(b));
};
